// In-memory queue backing the in-app notification banner stack. The
// `NotificationService.presentBanner` adapter pushes here; the
// `NotificationBannerHost` component renders the current items. In-memory
// only — banners are ephemeral and should never survive a reload.

import { create } from 'zustand';

import type { BannerPresentation } from './types';

/** Max banners visible at once; older ones drop off the bottom. */
export const MAX_VISIBLE_BANNERS = 3;

export interface BannerItem extends BannerPresentation {
  /** Epoch ms the banner was pushed (drives ordering + auto-dismiss). */
  readonly createdAt: number;
}

interface BannerState {
  readonly banners: readonly BannerItem[];
  push(banner: BannerPresentation, nowMs: number): void;
  dismiss(id: string): void;
  clear(): void;
}

export const useBannerStore = create<BannerState>((set) => ({
  banners: [],
  push: (banner, nowMs) =>
    set((prev) => {
      // Coalesce: a newer banner for the same conversation replaces the older
      // one (so a burst from one chat doesn't stack into a wall).
      const withoutSameConv = banner.conversationId
        ? prev.banners.filter((b) => b.conversationId !== banner.conversationId)
        : prev.banners;
      const next = [...withoutSameConv, { ...banner, createdAt: nowMs }];
      // Keep only the most recent MAX_VISIBLE_BANNERS.
      return { banners: next.slice(-MAX_VISIBLE_BANNERS) };
    }),
  dismiss: (id) =>
    set((prev) => ({ banners: prev.banners.filter((b) => b.id !== id) })),
  clear: () => set({ banners: [] }),
}));

/** Imperative push used by the platform presenter (outside React). */
export function pushBanner(banner: BannerPresentation): void {
  useBannerStore.getState().push(banner, Date.now());
}

/** Retroactively remove any banner for a given conversation (e.g. a call group
 *  banner that appeared before the call_invite arrived to suppress it). */
export function dismissBannersForConversation(conversationId: string): void {
  useBannerStore.setState((prev) => ({
    banners: prev.banners.filter((b) => b.conversationId !== conversationId),
  }));
}
